// Builds the app's single DataSource from runtime config. The whole app (and the
// rendering engine) shares this one instance. Swap the adapter here to change
// transports — no other file changes.

import { getConfig } from '../config/runtimeConfig';
import type { DataSource } from './DataSource';
import { DicomWebDataSource } from './adapters/DicomWebDataSource';

let instance: DataSource | null = null;
/** The DICOMweb root the shared instance reads, or null for the configured default. */
let boundRoot: string | null = null;

export function getDataSource(): DataSource {
  if (!instance) {
    instance = new DicomWebDataSource(getConfig().dicomWebRoot);
  }
  return instance;
}

/**
 * Which DICOMweb root the shared instance reads — part of every query key over
 * DICOMweb data. A UID is unique only inside a workspace, so two studies with
 * the same UID in two workspaces (or behind a share link) are different data;
 * keyed by UID alone, the second would be served the first one's cached series
 * and metadata.
 */
export function dataSourceRoot(): string {
  return boundRoot ?? getConfig().dicomWebRoot;
}

/**
 * Point the shared instance at a share link's own DICOMweb root.
 *
 * A share visitor talks to `/api/s/<shareId>/dicom-web`, not `/dicom-web` — the
 * namespace IS the authorisation, so the prefix is not cosmetic. Rebinding the
 * singleton rather than threading a second data source through the app is what
 * lets the existing study viewer work unchanged inside the share view: the
 * rendering engine reaches for getDataSource() itself, several layers down, and
 * would otherwise need a parameter on every call between here and there.
 *
 * A tab is either the app or a share view, never both, so one binding is enough
 * — but it MUST be undone on the way out, or navigating from a shared slide back
 * into the signed-in app would leave every query pointed at the share prefix.
 * resetDataSource is that undo, and the share pages call it on unmount.
 */
export function bindShareDataSource(shareId: string): void {
  boundRoot = `${getConfig().apiRoot}/s/${shareId}/dicom-web`;
  instance = new DicomWebDataSource(boundRoot);
}

/**
 * The DICOMweb root of one workspace: `<apiRoot>/w/<ws>/dicom-web`, beside the
 * share namespace's `<apiRoot>/s/<share>/dicom-web`.
 *
 * A StudyInstanceUID is unique only inside a workspace, so the root mount
 * answers for the caller's DEFAULT workspace alone — a study opened from
 * Explore, a notification or another workspace's collection lives somewhere
 * else, and asking the root for it finds nothing, or a different study that
 * happens to share the UID. Under the API root because that is a path every
 * deployment already routes to the server; a root of its own would reach the
 * CDN's app shell instead.
 */
export function workspaceDicomWebRoot(apiRoot: string, workspace: string): string {
  return `${apiRoot.replace(/\/+$/, '')}/w/${encodeURIComponent(workspace)}/dicom-web`;
}

/**
 * Point the shared instance at the workspace a study lives in (the study
 * head's `workspace`). Same contract as the share binding: bound before the
 * viewer mounts, and undone with resetDataSource on the way out, so a page
 * that reads the default workspace never inherits it.
 */
export function bindWorkspaceDataSource(workspace: string): void {
  boundRoot = workspaceDicomWebRoot(getConfig().apiRoot, workspace);
  instance = new DicomWebDataSource(boundRoot);
}

/** Back to the signed-in app's root. */
export function resetDataSource(): void {
  instance = null;
  boundRoot = null;
}
